import React, { useRef, useState } from "react";
import { Box, Portal } from "@mui/material";
import { useCustomTheme } from "../../theme";

const SLIDER_RADIUS = 6;
const THUMB_WIDTH = 4;
const THUMB_RADIUS = 1;
const THUMB_HEIGHT_PERCENT = 75;
const THUMB_OFFSET = 4;
const TRACK_RIGHT_OFFSET = 8;

const BrightnessBar = ({
  value,
  onChange,
  onValueUpdate,
  disabled = false,
  min = 0,
  max = 100,
  sliderRef,
}) => {
  const { theme } = useCustomTheme();
  const barRef = sliderRef || useRef(null);
  const dragging = useRef(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [showPercentage, setShowPercentage] = useState(false);
  const newValueRef = useRef(value);

  const percent = Math.max(0, Math.min(1, (value - min) / (max - min)));

  const getThumbCenter = () => {
    if (!barRef.current) return 0;
    const barWidth = barRef.current.offsetWidth;
    // thumb center moves from 4px to (barWidth - 4px)
    return THUMB_OFFSET + percent * (barWidth - 2 * THUMB_OFFSET);
  };

  const updatePosition = (clientX, clientY) => {
    if (!barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    setPosition({
      top: rect.top - 40,
      left: clientX,
    });
  };

  const handleMove = (clientX, clientY) => {
    if (!barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    let x = clientX - rect.left;
    x = Math.max(THUMB_WIDTH / 2, Math.min(rect.width - THUMB_WIDTH / 2, x));
    const newPercent = (x - THUMB_WIDTH / 2) / (rect.width - THUMB_WIDTH);
    const newValue = Math.round(newPercent * (max - min) + min);
    if (onChange) onChange({ stopPropagation: () => {} }, newValue);
    updatePosition(clientX, clientY);
    newValueRef.current = newValue;
  };

  const handleMouseDown = (e) => {
    if (disabled) return;
    dragging.current = true;
    setShowPercentage(true);
    handleMove(e.clientX, e.clientY);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!dragging.current) return;
    handleMove(e.clientX, e.clientY);
    setShowPercentage(true);
  };

  const handleMouseUp = (e) => {
    if (!dragging.current) return;
    dragging.current = false;
    setShowPercentage(false);
    onValueUpdate(newValueRef.current);
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (e) => {
    if (disabled) return;
    dragging.current = true;
    setShowPercentage(true);
    handleMove(e.touches[0].clientX, e.touches[0].clientY);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleTouchEnd);
  };

  const handleTouchMove = (e) => {
    if (!dragging.current) return;
    handleMove(e.touches[0].clientX, e.touches[0].clientY);
    setShowPercentage(true);
  };

  const handleTouchEnd = (e) => {
    if (!dragging.current) return;
    dragging.current = false;
    setShowPercentage(false);
    onValueUpdate(newValueRef.current);
    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleTouchEnd);
  };

  const getTrackWidth = () => {
    if (!barRef.current) return "0px";
    // track right = thumb center + (thumb width / 2) + 4px
    return `${getThumbCenter() + THUMB_WIDTH / 2 + THUMB_OFFSET}px`;
  };

  const thumbCenter = getThumbCenter();

  return (
    <Box
      ref={barRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        background: theme.palette.background.disabled,
        borderRadius: `${SLIDER_RADIUS}px`,
        overflow: "hidden",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.8 : 1,
        userSelect: "none",
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          height: "100%",
          width: getTrackWidth(),
          background: theme.palette.icon.y1,
          borderRadius: `${SLIDER_RADIUS}px`,
          transition: "width 0.2s",
          zIndex: 1,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: `${thumbCenter}px`,
          width: `${THUMB_WIDTH}px`,
          height: `${THUMB_HEIGHT_PERCENT}%`,
          background: theme.palette.background.l0,
          borderRadius: `${THUMB_RADIUS}px`,
          boxShadow: "0 0 4px rgba(0,0,0,0.3)",
          transform: "translate(-50%, -50%)",
          zIndex: 2,
          pointerEvents: disabled ? "none" : "auto",
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      />

      {showPercentage && (
        <Portal>
          <div
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              transform: "translateX(-50%)",
              backgroundColor: theme.palette.background.white,
              color: theme.palette.text.primary,
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: 500,
              minWidth: "40px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 2px 8px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.1)",
              pointerEvents: "none",
              userSelect: "none",
              zIndex: 99999,
              WebkitTransform: "translateX(-50%)",
              WebkitBackfaceVisibility: "hidden",
              backfaceVisibility: "hidden",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: theme.palette.background.white,
                borderRadius: "4px",
                opacity: 1,
              }}
            />
            <span
              style={{
                position: "relative",
                zIndex: 1,
                color: theme.palette.text.primary,
                fontWeight: 500,
              }}
            >
              {Math.round(value)}%
            </span>
          </div>
        </Portal>
      )}
    </Box>
  );
};

export default BrightnessBar;
